import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  DollarSign,
  ArrowUpRight,
  Link as LinkIcon,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  ExternalLink,
  Filter,
} from "lucide-react";
import { Link } from "react-router-dom";
import { format, parseISO, subDays } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import RatingComponent from "@/components/RatingComponent";

// Types
type OrderStatus = "pending" | "in_progress" | "completed" | "cancelled";

interface Order {
  id: string;
  created_at: string;
  buyer: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
  offer: {
    id: string;
    title: string;
    price: number;
  };
  status: OrderStatus;
  delivery_date?: string;
}

interface SellerStats {
  total_revenue: number;
  revenue_growth: number;
  total_orders: number;
  orders_growth: number;
  active_offers: number;
  avg_rating: number;
  total_reviews: number;
}

interface RevenueData {
  date: string;
  revenue: number;
}

const SellerDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  
  // Mock data for visualization - would be replaced with actual data from Supabase
  const mockRevenueData = Array.from({ length: 30 }, (_, i) => {
    const date = subDays(new Date(), 29 - i);
    return {
      date: format(date, "MMM dd"),
      revenue: Math.floor(Math.random() * 500) + 50,
    };
  });

  // Fetch seller stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["sellerStats", user?.id],
    queryFn: async (): Promise<SellerStats> => {
      // This would be an actual query to Supabase
      // For now we'll use mock data
      return {
        total_revenue: 12500,
        revenue_growth: 15.4,
        total_orders: 47,
        orders_growth: 8.2,
        active_offers: 7,
        avg_rating: 4.7,
        total_reviews: 32,
      };
    },
  });

  // Fetch recent orders
  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: ["sellerOrders", user?.id, { limit: 5 }],
    queryFn: async (): Promise<Order[]> => {
      // This would be a query to Supabase
      // For now we'll use mock data
      return [
        {
          id: "order-1",
          created_at: "2023-11-12T14:32:15Z",
          buyer: {
            id: "buyer-1",
            full_name: "Alex Johnson",
            avatar_url: "https://api.dicebear.com/7.x/lorelei/svg?seed=Alex",
          },
          offer: {
            id: "offer-1",
            title: "Technology Guest Post on DR70 Website",
            price: 180,
          },
          status: "in_progress",
          delivery_date: "2023-11-18T14:32:15Z",
        },
        {
          id: "order-2",
          created_at: "2023-11-10T09:15:22Z",
          buyer: {
            id: "buyer-2",
            full_name: "Samantha Lee",
            avatar_url: "https://api.dicebear.com/7.x/lorelei/svg?seed=Sam",
          },
          offer: {
            id: "offer-2",
            title: "Health Blog Backlink DA65",
            price: 220,
          },
          status: "completed",
          delivery_date: "2023-11-15T09:15:22Z",
        },
        {
          id: "order-3",
          created_at: "2023-11-08T16:45:33Z",
          buyer: {
            id: "buyer-3",
            full_name: "Michael Chen",
            avatar_url: "https://api.dicebear.com/7.x/lorelei/svg?seed=Mike",
          },
          offer: {
            id: "offer-3",
            title: "Finance Website Guest Post",
            price: 150,
          },
          status: "pending",
        },
        {
          id: "order-4",
          created_at: "2023-11-05T11:20:45Z",
          buyer: {
            id: "buyer-4",
            full_name: "Emma Wilson",
            avatar_url: "https://api.dicebear.com/7.x/lorelei/svg?seed=Emma",
          },
          offer: {
            id: "offer-4",
            title: "Technology News Website Link",
            price: 195,
          },
          status: "completed",
          delivery_date: "2023-11-09T11:20:45Z",
        },
        {
          id: "order-5",
          created_at: "2023-11-01T08:30:12Z",
          buyer: {
            id: "buyer-5",
            full_name: "David Kim",
            avatar_url: "https://api.dicebear.com/7.x/lorelei/svg?seed=David",
          },
          offer: {
            id: "offer-5",
            title: "Education Blog DA55 Backlink",
            price: 130,
          },
          status: "cancelled",
        },
      ];
    },
  });

  // Helper function to render status badge
  const renderStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      in_progress: { color: "bg-blue-100 text-blue-800", icon: ArrowUpRight },
      completed: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      cancelled: { color: "bg-red-100 text-red-800", icon: AlertCircle },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <div className={`flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        <span>{status.replace("_", " ")}</span>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Seller Dashboard | BackLink Hub</title>
      </Helmet>

      <div className="bg-gray-50 py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold">Seller Dashboard</h1>
              <p className="text-muted-foreground">
                Manage your offers, track your earnings, and grow your business
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filter
              </Button>
              <Button variant="outline" asChild>
                <Link to="/create-custom-package" className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Custom Package
                </Link>
              </Button>
              <Button asChild>
                <Link to="/create-offer" className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  New Offer
                </Link>
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
              <TabsTrigger value="offers">My Offers</TabsTrigger>
              <TabsTrigger value="earnings">Earnings</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Total Earnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline justify-between">
                      <div className="text-2xl font-bold">
                        ${stats?.total_revenue.toLocaleString()}
                      </div>
                      <div className="text-sm font-medium text-green-500 flex items-center">
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        {stats?.revenue_growth}%
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Completed Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline justify-between">
                      <div className="text-2xl font-bold">{stats?.total_orders}</div>
                      <div className="text-sm font-medium text-green-500 flex items-center">
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        {stats?.orders_growth}%
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Active Offers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline justify-between">
                      <div className="text-2xl font-bold">{stats?.active_offers}</div>
                      <Button variant="ghost" size="sm" className="text-xs" asChild>
                        <Link to="/create-offer">+ Add New</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Seller Rating
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline justify-between">
                      <div className="text-2xl font-bold">{stats?.avg_rating}</div>
                      <div className="text-sm text-muted-foreground">
                        {stats?.total_reviews} reviews
                      </div>
                    </div>
                    <div className="mt-1">
                      <RatingComponent value={stats?.avg_rating || 0} size="sm" readOnly />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Over Time</CardTitle>
                  <CardDescription>Daily earnings for the past 30 days</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={mockRevenueData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="#8884d8"
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Orders */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Recent Orders</CardTitle>
                    <CardDescription>Your latest 5 orders</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="#" onClick={() => setActiveTab("orders")}>
                      View All
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {orders?.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center space-x-4">
                          <Avatar>
                            <AvatarImage src={order.buyer.avatar_url} />
                            <AvatarFallback>
                              {order.buyer.full_name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{order.buyer.full_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {order.offer.title}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              ${order.offer.price} • {format(parseISO(order.created_at), "MMM dd, yyyy")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {renderStatusBadge(order.status)}
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="orders">
              <Card>
                <CardHeader>
                  <CardTitle>Order Management</CardTitle>
                  <CardDescription>View and manage all your orders</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>Orders table would go here with pagination and filtering</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="offers">
              <Card>
                <CardHeader>
                  <CardTitle>My Offers</CardTitle>
                  <CardDescription>Manage your active backlink offers</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>Offers table would go here with actions to edit or deactivate</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="earnings">
              <Card>
                <CardHeader>
                  <CardTitle>Earnings & Withdrawals</CardTitle>
                  <CardDescription>Track your earnings and withdrawal history</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>Earnings charts and withdrawal history would go here</p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default SellerDashboard;
